import React, { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { collection, getDocs } from 'firebase/firestore';
import { db } from '../firebase';
import { Icons } from '../lib/Icons';

function formatTermLabel(termId) {
  if (!termId) return 'Unknown term';
  const [season, year] = String(termId).split('-');
  const seasonLabel = season ? season.charAt(0).toUpperCase() + season.slice(1) : termId;
  return year ? `${seasonLabel} ${year}` : seasonLabel;
}

function formatGeneratedAt(plan) {
  const raw = plan?.generatedAt || plan?.createdAt || plan?.updatedAt;
  if (!raw) return 'Date unknown';
  const date = typeof raw?.toDate === 'function' ? raw.toDate() : new Date(raw);
  if (Number.isNaN(date.getTime())) return 'Date unknown';
  return date.toLocaleDateString('en-GB', { day: 'numeric', month: 'short', year: 'numeric' });
}

export default function TeachingPlanTabPanel({ studentId }) {
  const [plans, setPlans] = useState([]);
  const [loading, setLoading] = useState(true);

  const loadPlans = useCallback(async () => {
    if (!studentId) return;
    setLoading(true);
    try {
      const snap = await getDocs(collection(db, 'teachingPlans', studentId, 'terms'));
      const list = snap.docs
        .map((docSnap) => ({ id: docSnap.id, ...docSnap.data() }))
        .sort((a, b) => String(b.id).localeCompare(String(a.id)));
      setPlans(list);
    } catch (error) {
      console.error('Failed to load teaching plans:', error);
      setPlans([]);
    } finally {
      setLoading(false);
    }
  }, [studentId]);

  useEffect(() => {
    loadPlans();
  }, [loadPlans]);

  if (loading) {
    return <p className="text-sm text-gray-500">Loading teaching plans...</p>;
  }

  if (plans.length === 0) {
    return (
      <div className="rounded-xl border border-dashed border-gray-300 bg-white p-8 text-center">
        <div className="mx-auto mb-4 flex h-14 w-14 items-center justify-center rounded-2xl bg-green-50">
          <Icons.BookOpen className="h-7 w-7 text-green-600" />
        </div>
        <h3 className="text-lg font-semibold text-gray-900">No teaching plan yet</h3>
        <p className="mx-auto mt-2 max-w-lg text-sm text-gray-600">
          Complete the classroom questionnaire to generate matched activity tiles and a weekly timetable
          for this pupil.
        </p>
        <Link
          to={`/students/${studentId}/teaching-plan-questionnaire`}
          className="mt-6 inline-flex items-center rounded-md bg-green-600 px-4 py-2 text-sm font-medium text-white shadow-sm hover:bg-green-700"
        >
          Start Teaching Plan Questionnaire
        </Link>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <p className="text-sm text-gray-600">
            Generated teaching plans from the classroom questionnaire: matched KB activities and weekly timetables.
          </p>
        </div>
        <Link
          to={`/students/${studentId}/teaching-plan-questionnaire`}
          className="inline-flex items-center rounded-md border border-green-600 bg-white px-3 py-2 text-sm font-medium text-green-700 hover:bg-green-50"
        >
          Start new questionnaire
        </Link>
      </div>

      <ul className="divide-y divide-gray-200 overflow-hidden rounded-xl border border-gray-200 bg-white">
        {plans.map((plan) => (
          <li key={plan.id} className="flex flex-wrap items-center justify-between gap-3 px-4 py-4">
            <div>
              <p className="font-medium text-gray-900">{formatTermLabel(plan.id)}</p>
              <p className="text-sm text-gray-500">
                {plan.activities?.length || 0} activities · Generated {formatGeneratedAt(plan)}
              </p>
            </div>
            <Link
              to={`/students/${studentId}/teaching-plan?term=${encodeURIComponent(plan.id)}`}
              className="inline-flex items-center rounded-md bg-blue-600 px-3 py-2 text-sm font-medium text-white hover:bg-blue-700"
            >
              Open plan
            </Link>
          </li>
        ))}
      </ul>
    </div>
  );
}
